/**
 * The company's task library.
 *
 * A task is created by typing a name in the shift drawer, and the definition is kept for reuse:
 * the picker offers what the company has used before. Until 2026-09-20 there was no way to change
 * one afterwards. Renaming a task in the drawer renames it on that shift only, because the shift
 * routes find-or-create by (tenant_id, name), so a new spelling makes a second definition and
 * leaves the first in the picker for good. Three of the nineteen definitions on the live data have
 * no shift pointing at them, which is what that looks like after a year.
 *
 * This page writes to the definition itself, PUT /admin/shift-tasks/:id, so every shift already
 * carrying the task follows. Two things about it are worth knowing before reading the code:
 *
 * Renaming onto a name the company already uses MERGES rather than failing. The server moves the
 * assignments to the row that already has the name and deletes the emptied one. That is the only
 * way to join two definitions that are the same task spelled differently, which is the mess this
 * page exists to clean up, so it is announced when it happens rather than done quietly.
 *
 * Retiring is not deleting. There is no delete route, and there should not be: a definition with
 * shifts on it cannot go without taking their task rows with it. `is_active: false` takes it out
 * of the picker and leaves every shift that has it alone.
 */
import { type ApiShiftTask, useShiftTaskLibrary, useShiftTaskWrites } from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { Button, Empty, Panel, toast } from '@/ui';
import { Icon } from '@/ui/icons';
import { useState } from 'react';
import { IconPick } from './IconPick';
import { ColorPick, Pakker } from './Pakker';

const uses = (t: ApiShiftTask) => Number(t.in_use ?? 0);

export default function Oppgaver() {
  const can = useCan();
  const mayEdit = can('vaktplan.endre');
  const q = useShiftTaskLibrary();
  const w = useShiftTaskWrites();
  const [problem, setProblem] = useState<string | null>(null);

  const rows = [...(q.data ?? [])].sort(
    (a, b) => Number(b.is_active) - Number(a.is_active) || a.name.localeCompare(b.name, 'nb'),
  );

  const save = async (t: ApiShiftTask, body: Parameters<typeof w.update.mutateAsync>[0]['body']) => {
    setProblem(null);
    try {
      const next = await w.update.mutateAsync({ id: t.id, body });
      if (next.merged_from != null) {
        // the row the person was editing no longer exists; say so, because the list is about to
        // get shorter and a silent disappearance reads as a bug
        toast(T('«{old}» er slått sammen med «{new}».', { old: t.name, new: next.name }));
      } else if (body.is_active === false) {
        toast(T('«{task}» er pensjonert og vises ikke lenger i plukklisten.', { task: next.name }));
      } else if (body.color !== undefined) {
        toast(T('Fargen er lagret.'));
      } else if (body.icon !== undefined) {
        toast(body.icon ? T('Ikonet er lagret.') : T('Ikonet velges ut fra navnet igjen.'));
      } else if (body.is_active === true) {
        toast(T('«{task}» er tilbake i plukklisten.', { task: next.name }));
      } else {
        toast(T('Lagret'));
      }
    } catch (e) {
      setProblem(e instanceof Error ? e.message : T('Kunne ikke lagre.'));
    }
  };

  const add = async () => {
    setProblem(null);
    try {
      await w.create.mutateAsync(T('Ny aktivitet {n}', { n: rows.length + 1 }));
      toast(T('Aktivitet opprettet'));
    } catch (e) {
      setProblem(e instanceof Error ? e.message : T('Kunne ikke opprette.'));
    }
  };

  return (
    <>
      <Panel
        title={T('Aktiviteter')}
        sub={T('bedriftens gjenbrukbare aktiviteter, slik de tilbys når du lager en vakt')}
        actions={
          mayEdit ? (
            <Button small onClick={add}>
              <Icon name="plus" />
              {T('Ny aktivitet')}
            </Button>
          ) : null
        }
        flush
      >
        <p className="hint" style={{ padding: '0 12px' }}>
          {T(
            'Endrer du navnet her, følger alle vaktene som allerede har aktiviteten. Gir du den et navn bedriften allerede bruker, slås de to sammen.',
          )}
        </p>
        {rows.length ? (
          <table className="t" data-testid="task-library">
            <thead>
              <tr>
                <th>{T('Ikon')}</th>
                <th>{T('Farge')}</th>
                <th>{T('Aktivitet')}</th>
                <th>{T('Beskrivelse')}</th>
                <th>{T('Standard estimat (min)')}</th>
                <th>{T('Dokumentasjon')}</th>
                <th className="n">{T('På vakter')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} data-testid={t.is_active ? 'task-live' : 'task-retired'}>
                  <td>
                    <IconPick
                      task={t.name}
                      icon={t.icon}
                      disabled={!mayEdit}
                      onPick={(icon) => save(t, { icon })}
                    />
                  </td>
                  <td>
                    {/* the colour on the shift's timeline (#202) */}
                    <ColorPick
                      task={t.name}
                      color={t.color}
                      disabled={!mayEdit}
                      onPick={(color) => save(t, { color })}
                    />
                  </td>
                  <td>
                    <input
                      className="inp"
                      aria-label={T('Navn på {task}', { task: t.name })}
                      key={t.name}
                      defaultValue={t.name}
                      disabled={!mayEdit}
                      style={{ minWidth: 200, textDecoration: t.is_active ? undefined : 'line-through' }}
                      onBlur={(e) => {
                        const name = e.target.value.trim();
                        if (name && name !== t.name) save(t, { name });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      className="inp w"
                      aria-label={T('Beskrivelse av {task}', { task: t.name })}
                      key={t.description ?? ''}
                      defaultValue={t.description ?? ''}
                      disabled={!mayEdit}
                      placeholder={T('Beskrivelse (valgfritt)')}
                      onBlur={(e) => {
                        const description = e.target.value.trim() || null;
                        if (description !== (t.description ?? null)) save(t, { description });
                      }}
                    />
                  </td>
                  <td>
                    {/* what a new task of this type in Oppgaver starts with (#293); tasks already made keep theirs */}
                    <input
                      className="inp"
                      inputMode="numeric"
                      aria-label={T('Standard estimat for {task} i minutter', { task: t.name })}
                      key={`e${t.default_estimate_minutes ?? ''}`}
                      defaultValue={t.default_estimate_minutes ?? ''}
                      disabled={!mayEdit}
                      style={{ width: 80 }}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        const n = raw === '' ? null : Number(raw);
                        if (n !== null && (!Number.isInteger(n) || n < 0)) {
                          setProblem(T('Estimatet må være et helt antall minutter, 0 eller mer.'));
                          return;
                        }
                        if (n !== (t.default_estimate_minutes ?? null))
                          save(t, { default_estimate_minutes: n });
                      }}
                    />
                  </td>
                  <td>
                    <select
                      className="inp"
                      aria-label={T('Dokumentasjon for {task}', { task: t.name })}
                      value={t.requirement ?? ''}
                      disabled={!mayEdit}
                      onChange={(e) =>
                        save(t, { requirement: (e.target.value || null) as 'comment' | 'measurement' | null })
                      }
                    >
                      <option value="">{T('Ingen krav')}</option>
                      <option value="comment">{T('Kommentar')}</option>
                      <option value="measurement">{T('Måling (tall)')}</option>
                    </select>
                  </td>
                  <td className="n">{uses(t)}</td>
                  <td>
                    {mayEdit ? (
                      <Button
                        small
                        ghost={t.is_active}
                        aria-label={
                          t.is_active
                            ? T('Pensjoner {task}', { task: t.name })
                            : T('Ta {task} i bruk igjen', { task: t.name })
                        }
                        onClick={() => save(t, { is_active: !t.is_active })}
                      >
                        {t.is_active ? T('Pensjoner') : T('Ta i bruk')}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>{T('Ingen aktiviteter ennå. De lages når du legger en aktivitet på en vakt.')}</Empty>
        )}
        {problem ? (
          <p className="hint" data-testid="task-library-problem" style={{ padding: '0 12px' }}>
            {problem}
          </p>
        ) : null}
      </Panel>
      <Pakker mayEdit={mayEdit} />
    </>
  );
}
